import { countryLabel, dialCode } from "@/lib/countries";

// Lot 560 — vérification du numéro WhatsApp selon le plan de numérotation du pays (libphonenumber-js/min,
// chargé à la demande). check = { status: unknown | ok | short | long | length | doubtful, iso, lengths }.
export const EMPTY_CHECK = { status: "unknown", iso: "", lengths: [] };
export const BLOCKING = new Set(["short", "long", "length"]);
export const isBlockingCheck = (check) => !!check && BLOCKING.has(check.status);

let libPromise = null;
export const loadPhoneLib = () => {
  if (!libPromise) {
    libPromise = Promise.all([import("libphonenumber-js/min"), import("libphonenumber-js/min/metadata")])
      .then(([lib, md]) => ({ lib, metadata: md.default || md }));
  }
  return libPromise;
};

const STATUS_BY_LENGTH = { TOO_SHORT: "short", TOO_LONG: "long", INVALID_LENGTH: "length" };

export const checkPhone = async (candidate, isoHint = "") => {
  const digits = (candidate || "").replace(/\D/g, "");
  if (digits.length < 4) return EMPTY_CHECK;
  const { lib, metadata } = await loadPhoneLib();
  const e164 = `+${digits}`;
  const parsed = lib.parsePhoneNumberFromString(e164);
  let iso = (parsed && parsed.country) || isoHint || "";
  if (!iso && parsed) {
    const codes = lib.getCountries().filter((c) => lib.getCountryCallingCode(c) === parsed.countryCallingCode);
    iso = codes.includes("US") ? "US" : codes[0] || "";
  }
  let lengths = [];
  if (iso) {
    const m = new lib.Metadata(metadata);
    if (m.hasCountry(iso)) { m.country(iso); lengths = m.possibleLengths() || []; }
  }
  const len = lib.validatePhoneNumberLength(e164);
  if (len === "NOT_A_NUMBER" || len === "INVALID_COUNTRY") return { ...EMPTY_CHECK, iso };
  if (STATUS_BY_LENGTH[len]) return { status: STATUS_BY_LENGTH[len], iso, lengths };
  return { status: parsed && !parsed.isValid() ? "doubtful" : "ok", iso, lengths };
};

// [9] → « 9 » ; [8, 9] → « 8 ou 9 » ; [7, 8, 9, 10] → « 7 à 10 » ; sinon liste.
export const formatLengths = (lengths, t) => {
  const l = [...(lengths || [])].sort((a, b) => a - b);
  if (!l.length) return "";
  if (l.length === 1) return String(l[0]);
  const contiguous = l.every((v, i) => i === 0 || v === l[i - 1] + 1);
  if (l.length === 2) return t("phone.lengths_or", "{a} ou {b}").replace("{a}", l[0]).replace("{b}", l[1]);
  if (contiguous) return t("phone.lengths_range", "{a} à {b}").replace("{a}", l[0]).replace("{b}", l[l.length - 1]);
  return l.join(", ");
};

const KEYS = {
  // Lot 632 — nouvelle clé : l'ancienne « phone.check_short » (app native 2.1 figée) porte un conseil temporaire pour les 4 pays dont le 0 est significatif.
  short: ["phone.check_short2", "Numéro trop court pour {country} : {n} chiffres attendus après +{dial}."],
  long: ["phone.check_long", "Numéro trop long pour {country} : {n} chiffres attendus après +{dial}."],
  length: ["phone.check_length", "Ce numéro n'a pas le bon nombre de chiffres pour {country} ({n} attendus après +{dial})."],
  doubtful: ["phone.check_doubtful", "Ce numéro ne ressemble pas à un numéro de {country}. Vérifie-le avant de recevoir le code."],
};

export const phoneIssueMessage = (check, t, lang) => {
  const k = KEYS[check?.status];
  if (!k) return "";
  const dial = dialCode(check.iso);
  const country = countryLabel(check.iso, lang) || (dial ? `+${dial}` : "");
  return t(k[0], k[1]).replace("{country}", country).replace("{n}", formatLengths(check.lengths, t)).replace("{dial}", dial);
};
